import React, { useState, useEffect, useCallback } from "react";
import { API_BASE_URL } from "../../config";

const PAYMENT_CODE_API = `${API_BASE_URL}/saledata/api/module/payment-codes`;

// Three buckets: DeliveryRE's existing payment codes (M-1/M-20/.../BT), the tablet
// app's end-of-route cash count, and its expense-entry dropdown. One admin page so
// none of these are hardcoded in app config anymore (vector 2026-07-06).
// CASH_COIN = เหรียญย่อย 2/5/10 ที่เลิกนับแยกแล้ว (Vector 2026-09-21 สั่งรวม 1/2/5/10 เป็น
// ช่องเดียว) แถวยังอยู่เพราะ payment_data เก่าอ้างรหัสพวกนี้ แต่ไม่อยู่ในหมวดที่เครื่องดึงไปสร้าง
// ช่องกรอก ⇒ ต้องมีในลิสต์นี้ ไม่งั้นแอดมินเปิดหน้านี้จะเห็นแถวเป็นหมวดว่างและย้ายกลับไม่ได้
const CATEGORIES = [
    { value: "PAYMENT", label: "การชำระเงิน (DeliveryRE)" },
    { value: "CASH_DENOMINATION", label: "เงินสด (นับตอนปิดรอบ — App)" },
    { value: "CASH_COIN", label: "เหรียญย่อย (เลิกนับแยกแล้ว — ไม่ขึ้นบนเครื่อง)" },
    { value: "EXPENSE", label: "ค่าใช้จ่ายระหว่างทาง (App)" },
];

const emptyForm = { code: "", name: "", paymentValue: "", description: "", category: "" };

function getToken() {
    try {
        const user = JSON.parse(localStorage.getItem("user"));
        return user?.token || "";
    } catch { return ""; }
}

function authHeaders() {
    return { "Content-Type": "application/json", Authorization: `Bearer ${getToken()}` };
}

async function api(method, path, body) {
    const r = await fetch(`${PAYMENT_CODE_API}${path}`, {
        method,
        headers: authHeaders(),
        body: body ? JSON.stringify(body) : undefined,
    });
    if (r.status === 204) return null;
    if (!r.ok) {
        const text = await r.text().catch(() => "");
        throw new Error(text || `${r.status} ${r.statusText}`);
    }
    return r.json();
}

export default function PaymentCodeAdmin() {
    const [refs, setRefs] = useState([]);
    const [filter, setFilter] = useState("");
    const [form, setForm] = useState(emptyForm);
    const [editingCode, setEditingCode] = useState(null); // null = creating new
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    const load = useCallback(async () => {
        setError("");
        try {
            setRefs(await api("GET", ""));
        } catch (e) {
            setError(e.message || "โหลดไม่สำเร็จ");
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    function startEdit(ref) {
        setEditingCode(ref.code);
        setForm({
            code: ref.code,
            name: ref.name || "",
            paymentValue: ref.paymentValue != null ? String(ref.paymentValue) : "",
            description: ref.description || "",
            category: ref.category || "",
        });
    }

    function cancelEdit() {
        setEditingCode(null);
        setForm(emptyForm);
    }

    async function save() {
        if (!form.code.trim() && !editingCode) { setError("กรุณากรอกรหัส (code)"); return; }
        if (!form.category) { setError("กรุณาเลือกหมวด (category)"); return; }
        setLoading(true);
        setError("");
        const body = {
            code: editingCode || form.code.trim(),
            name: form.name.trim(),
            paymentValue: form.paymentValue !== "" ? parseFloat(form.paymentValue) : null,
            description: form.description.trim(),
            category: form.category,
        };
        try {
            if (editingCode) {
                await api("PUT", `/${encodeURIComponent(editingCode)}`, body);
            } else {
                await api("POST", "", body);
            }
            cancelEdit();
            await load();
        } catch (e) {
            setError(e.message || "บันทึกไม่สำเร็จ");
        } finally {
            setLoading(false);
        }
    }

    async function remove(code) {
        if (!window.confirm(`ยืนยันลบ payment ref "${code}"?`)) return;
        setError("");
        try {
            await api("DELETE", `/${encodeURIComponent(code)}`);
            await load();
        } catch (e) {
            setError(e.message || "ลบไม่สำเร็จ");
        }
    }

    const visibleRefs = filter ? refs.filter(r => r.category === filter) : refs;

    return (
        <div className="btc-container">
            <h1>Payment Ref — เงินสด / ค่าใช้จ่าย / การชำระเงิน</h1>

            <div className="btc-card" style={{ padding: "16px 24px", marginBottom: 16 }}>
                <h3 style={{ marginTop: 0 }}>{editingCode ? `แก้ไข "${editingCode}"` : "สร้าง Ref ใหม่"}</h3>
                {error && <p className="error">{error}</p>}
                <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                    <input
                        type="text"
                        placeholder="code เช่น CASH-1000"
                        value={form.code}
                        disabled={!!editingCode}
                        onChange={e => setForm(f => ({ ...f, code: e.target.value }))}
                        style={{ padding: "6px 10px", borderRadius: 4, border: "1px solid #E5E5E5", minWidth: 160 }}
                    />
                    <input
                        type="text"
                        placeholder="ชื่อ (name)"
                        value={form.name}
                        onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                        style={{ padding: "6px 10px", borderRadius: 4, border: "1px solid #E5E5E5", minWidth: 160 }}
                    />
                    <input
                        type="number"
                        placeholder="มูลค่า (paymentValue)"
                        value={form.paymentValue}
                        onChange={e => setForm(f => ({ ...f, paymentValue: e.target.value }))}
                        style={{ padding: "6px 10px", borderRadius: 4, border: "1px solid #E5E5E5", minWidth: 140 }}
                    />
                    <input
                        type="text"
                        placeholder="คำอธิบาย (description)"
                        value={form.description}
                        onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                        style={{ padding: "6px 10px", borderRadius: 4, border: "1px solid #E5E5E5", minWidth: 200 }}
                    />
                    <select
                        value={form.category}
                        onChange={e => setForm(f => ({ ...f, category: e.target.value }))}
                        style={{ padding: "6px 10px", borderRadius: 4, border: "1px solid #E5E5E5", minWidth: 200 }}
                    >
                        <option value="">-- เลือกหมวด --</option>
                        {CATEGORIES.map(c => (
                            <option key={c.value} value={c.value}>{c.label}</option>
                        ))}
                    </select>
                    <button className="btc-btn btn-sm" style={{ backgroundColor: "#343a40" }} onClick={save} disabled={loading}>
                        {loading ? "กำลังบันทึก..." : editingCode ? "บันทึก" : "+ สร้าง"}
                    </button>
                    {editingCode && (
                        <button className="btc-btn btn-sm" style={{ backgroundColor: "#6B6B6B" }} onClick={cancelEdit}>
                            ยกเลิก
                        </button>
                    )}
                </div>
            </div>

            <div className="btc-card" style={{ padding: "16px 24px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                    <h3 style={{ margin: 0 }}>Payment Refs ทั้งหมด — {visibleRefs.length} รายการ</h3>
                    <select
                        value={filter}
                        onChange={e => setFilter(e.target.value)}
                        style={{ padding: "6px 10px", borderRadius: 4, border: "1px solid #E5E5E5" }}
                    >
                        <option value="">ทุกหมวด</option>
                        {CATEGORIES.map(c => (
                            <option key={c.value} value={c.value}>{c.label}</option>
                        ))}
                    </select>
                </div>
                {visibleRefs.length === 0
                    ? <p>ยังไม่มี payment ref</p>
                    : (
                        <table className="btc-table" style={{ width: "100%", fontSize: 13 }}>
                            <thead>
                                <tr>
                                    <th>Code</th>
                                    <th>ชื่อ</th>
                                    <th>มูลค่า</th>
                                    <th>คำอธิบาย</th>
                                    <th>หมวด</th>
                                    <th style={{ width: 80 }}>สถานะ</th>
                                    <th style={{ width: 140 }}>จัดการ</th>
                                </tr>
                            </thead>
                            <tbody>
                                {visibleRefs.map(r => (
                                    <tr key={r.code}>
                                        <td>{r.code}</td>
                                        <td>{r.name || "-"}</td>
                                        <td>{r.paymentValue != null ? r.paymentValue.toLocaleString() : "-"}</td>
                                        <td>{r.description || "-"}</td>
                                        <td>{CATEGORIES.find(c => c.value === r.category)?.label || r.category || "-"}</td>
                                        <td>
                                            {r.locked && (
                                                <span style={{
                                                    background: "#E5E5E5", color: "#000",
                                                    borderRadius: 10, padding: "2px 8px", fontSize: 12, fontWeight: 600,
                                                }}>
                                                    locked
                                                </span>
                                            )}
                                        </td>
                                        <td>
                                            {!r.locked && (
                                                <>
                                                    <button className="btc-btn btn-sm" style={{ backgroundColor: "#343a40", marginRight: 6 }} onClick={() => startEdit(r)}>
                                                        แก้ไข
                                                    </button>
                                                    <button className="btc-btn btn-sm" style={{ backgroundColor: "#E60012" }} onClick={() => remove(r.code)}>
                                                        ลบ
                                                    </button>
                                                </>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )
                }
            </div>
        </div>
    );
}
